import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ApiError, getAIAnalyticsConversationDetail } from "@/lib/api";
import { ArrowLeft, User, Bot, Image, Check, X } from "lucide-react";

export function AIConversationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: conversation,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["ai-conversation-detail", id],
    queryFn: () => getAIAnalyticsConversationDetail(id!),
    enabled: !!id,
    retry: (failureCount, err) =>
      !(err instanceof ApiError && err.status === 404) && failureCount < 2,
  });

  if (isLoading) {
    return <div className="p-8">Načítám…</div>;
  }

  if (error) {
    const isNotFound = error instanceof ApiError && error.status === 404;
    return (
      <div className="p-8 space-y-4">
        <h1 className="text-xl font-bold">
          {isNotFound
            ? "Konverzace nenalezena"
            : "Detail konverzace se nepodařilo načíst"}
        </h1>
        {!isNotFound && (
          <p className="text-sm text-muted-foreground max-w-prose">
            {error.message}
          </p>
        )}
        <div className="flex gap-2">
          {!isNotFound && (
            <Button variant="outline" onClick={() => refetch()}>
              Zkusit znovu
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={() => navigate("/menu-editor/ai-asistent")}
          >
            Zpět na přehled
          </Button>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return <div className="p-8">Konverzace nenalezena</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/menu-editor/ai-asistent")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Detail konverzace</h1>
      </div>

      {/* Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Informace</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-muted-foreground">Datum</div>
            <div>
              {new Date(conversation.created_at).toLocaleString("cs-CZ")}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Tema</div>
            <Badge variant="outline">
              {conversation.topic_category || "N/A"}
            </Badge>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Vysledek</div>
            <Badge>{conversation.resolution_type || "N/A"}</Badge>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Naklady</div>
            <div>${conversation.cost_usd?.toFixed(4) || "0"}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Tokeny</div>
            <div>
              {conversation.tokens_in} in / {conversation.tokens_out} out
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Zprav</div>
            <div>{conversation.session_turns}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Navrhy</div>
            <div>
              <span className="text-green-600">
                {conversation.suggestions_accepted}
              </span>
              {" / "}
              <span className="text-red-600">
                {conversation.suggestions_rejected}
              </span>
              {" / "}
              {conversation.suggestions_count}
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">Feedback</div>
            <Badge
              variant={
                conversation.outcome === "helpful" ? "default" : "secondary"
              }
            >
              {conversation.outcome || "N/A"}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Messages */}
      <Card>
        <CardHeader>
          <CardTitle>Zpravy</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {conversation.messages.map((msg, i) => (
            <div
              key={i}
              className={`flex gap-3 ${msg.role === "assistant" ? "flex-row-reverse" : ""}`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  msg.role === "user" ? "bg-blue-100" : "bg-green-100"
                }`}
              >
                {msg.role === "user" ? (
                  <User className="h-4 w-4 text-blue-600" />
                ) : (
                  <Bot className="h-4 w-4 text-green-600" />
                )}
              </div>
              <div
                className={`max-w-[80%] rounded-lg p-3 ${
                  msg.role === "user" ? "bg-blue-50" : "bg-green-50"
                }`}
              >
                {msg.has_screenshot && (
                  <Badge variant="outline" className="mb-2">
                    <Image className="h-3 w-3 mr-1" />
                    Screenshot
                  </Badge>
                )}
                <div className="whitespace-pre-wrap">{msg.content}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Suggestions */}
      {conversation.suggestions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Navrhy zmen</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {conversation.suggestions.map((sug) => (
              <div
                key={sug.id}
                className="border rounded-lg p-4 flex items-start justify-between"
              >
                <div className="space-y-2">
                  <div className="font-mono text-sm">{sug.setting_path}</div>
                  <div className="text-sm text-muted-foreground">
                    {JSON.stringify(sug.old_value)} &rarr;{" "}
                    {JSON.stringify(sug.new_value)}
                  </div>
                  {sug.explanation && (
                    <div className="text-sm">{sug.explanation}</div>
                  )}
                </div>
                <Badge
                  variant={
                    sug.status === "accepted"
                      ? "default"
                      : sug.status === "rejected"
                        ? "destructive"
                        : "secondary"
                  }
                >
                  {sug.status === "accepted" && (
                    <Check className="h-3 w-3 mr-1" />
                  )}
                  {sug.status === "rejected" && <X className="h-3 w-3 mr-1" />}
                  {sug.status}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
